import { useState, useEffect } from "react";
import { Link } from "wouter";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Search, Star, Shield, Clock, MapPin, Heart, ChevronRight,
  CheckCircle, Users, Award, TrendingUp, Phone, ArrowRight,
  Activity, Zap, Lock, Headphones
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useListTopRatedProviders, useListSpecialties } from "@/lib/api-client-react";

const fadeUp: import("framer-motion").Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const } })
};

const stats = [
  { label: "Verified Providers", value: "50,000+" },
  { label: "Patient Visits", value: "2M+" },
  { label: "Insurance Plans", value: "300+" },
  { label: "Cities Covered", value: "500+" },
];

const features = [
  { icon: Search, title: "Instant Search", desc: "Find the right provider in seconds by specialty, location, insurance, and availability." },
  { icon: Shield, title: "Verified & Trusted", desc: "Every provider is credentialed, licensed, and reviewed by real patients." },
  { icon: Calculator, title: "Cost Transparency", desc: "See real cost estimates before your appointment. No surprises." },
  { icon: Users, title: "Care Team", desc: "Build a coordinated care team and share health records securely." },
  { icon: Zap, title: "Telehealth Ready", desc: "Connect with providers via video from anywhere, anytime." },
  { icon: Lock, title: "HIPAA Compliant", desc: "Your health data is encrypted and protected at every step." },
];

const howItWorks = [
  { step: "01", title: "Search Providers", desc: "Enter your location, specialty, or insurance to find the right care." },
  { step: "02", title: "Compare & Book", desc: "Review profiles, check availability, and book instantly — no phone tag." },
  { step: "03", title: "Get Estimates", desc: "See upfront cost estimates so you can make informed decisions." },
  { step: "04", title: "Manage Your Care", desc: "Track appointments, build your care team, and access your records." },
];

const testimonials = [
  { name: "Sarah M.", role: "Patient", rating: 5, text: "StraightDoc made finding a specialist so easy. I had an appointment within 2 days and the cost estimate was spot on." },
  { name: "Dr. James Chen", role: "Cardiologist", rating: 5, text: "The provider portal is incredibly well designed. Managing patients and GFEs has never been this streamlined." },
  { name: "Robert K.", role: "Patient", rating: 5, text: "I was shocked to see exactly what my insurance would cover before my procedure. Total game changer." },
  { name: "Dr. Amara Singh", role: "Primary Care", rating: 5, text: "My patients love being able to book online and see their care team in one place. Highly recommend." },
];

const faqs = [
  { q: "Is StraightDoc free for patients?", a: "Yes, StraightDoc is completely free for patients. We make our money by working with healthcare providers and insurance companies." },
  { q: "How accurate are the cost estimates?", a: "Our estimates are based on real claims data and your insurance plan details, typically accurate within 5–10% of actual costs." },
  { q: "Is my health data secure?", a: "Absolutely. We are fully HIPAA compliant with end-to-end encryption and SOC 2 Type II certification." },
  { q: "Can I use StraightDoc without insurance?", a: "Yes. We show self-pay rates and help you find providers that offer transparent cash-pay pricing." },
];

function Calculator({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="4" y="2" width="16" height="20" rx="2" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="8" y1="10" x2="10" y2="10" /><line x1="14" y1="10" x2="16" y2="10" /><line x1="8" y1="14" x2="10" y2="14" /><line x1="14" y1="14" x2="16" y2="14" /><line x1="8" y1="18" x2="16" y2="18" />
    </svg>
  );
}

export default function Landing() {
  const [searchQuery, setSearchQuery] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { data: topProviders } = useListTopRatedProviders();
  const { data: specialties } = useListSpecialties();

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/80 dark:bg-card/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-xl">StraightDoc</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-foreground transition-colors">How It Works</a>
            <a href="#testimonials" className="hover:text-foreground transition-colors">Reviews</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link href="/login">
              <Button variant="ghost" size="sm" data-testid="button-login">Sign In</Button>
            </Link>
            <Link href="/register">
              <Button size="sm" className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white border-0" data-testid="button-get-started">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary/5 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible">
              <Badge className="mb-6 bg-primary/10 text-primary border-0 px-4 py-1.5 text-sm font-medium">
                Trusted by 2 million patients
              </Badge>
            </motion.div>
            <motion.h1 custom={1} variants={fadeUp} initial="hidden" animate="visible" className="text-5xl sm:text-6xl font-bold tracking-tight text-foreground leading-tight mb-6">
              Healthcare that works{" "}
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                for you
              </span>
            </motion.h1>
            <motion.p custom={2} variants={fadeUp} initial="hidden" animate="visible" className="text-xl text-muted-foreground mb-10 leading-relaxed">
              Find trusted providers, see real cost estimates, and manage your entire care journey — all in one beautifully simple platform.
            </motion.p>

            <motion.div custom={3} variants={fadeUp} initial="hidden" animate="visible" className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto mb-10">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by specialty, provider name..."
                  className="pl-11 h-12 text-base rounded-xl bg-white dark:bg-card border-border shadow-sm"
                  data-testid="input-hero-search"
                />
              </div>
              <Link href="/login">
                <Button size="lg" className="h-12 px-8 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white border-0 rounded-xl font-semibold" data-testid="button-find-care">
                  Find Care
                </Button>
              </Link>
            </motion.div>

            <motion.div custom={4} variants={fadeUp} initial="hidden" animate="visible" className="flex flex-wrap justify-center gap-2">
              {(specialties && specialties.length > 0
                ? specialties.slice(0, 6).map((s: any) => typeof s === 'string' ? s : (s.name || s.label || s.code))
                : []
              ).map((s: string) => (
                <Link key={s} href="/login">
                  <Badge variant="secondary" className="cursor-pointer hover:bg-accent px-3 py-1.5 text-sm" data-testid={`badge-specialty-${s}`}>
                    {s}
                  </Badge>
                </Link>
              ))}
            </motion.div>
          </div>

          {/* Floating Cards */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {(topProviders ?? []).slice(0, 3).map((p, i) => (
              <motion.div
                key={p.id}
                custom={i + 5}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                whileHover={{ y: -4, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                className="bg-white dark:bg-card rounded-2xl p-5 border border-border shadow-sm cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-primary">{p.name.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.specialty}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      <span className="text-xs font-medium">{p.rating}</span>
                      <span className="text-xs text-muted-foreground">({p.reviewCount})</span>
                    </div>
                  </div>
                </div>
                {p.nextAvailable && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-primary">
                    <Clock className="w-3 h-3" />
                    <span>Next: {p.nextAvailable}</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <p className="text-3xl sm:text-4xl font-bold mb-1">{s.value}</p>
                <p className="text-primary-foreground/70 text-sm">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary border-0">Why StraightDoc</Badge>
            <h2 className="text-4xl font-bold mb-4">Everything you need for better care</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">Built with patients and providers in mind — so both sides of healthcare work seamlessly together.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -3 }}
                className="bg-white dark:bg-card rounded-2xl p-6 border border-border shadow-sm"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-secondary/15 flex items-center justify-center mb-4">
                  <f.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{f.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-4 sm:px-6 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <motion.div custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <Badge className="mb-4 bg-secondary/10 text-secondary border-0">Simple Process</Badge>
            <h2 className="text-4xl font-bold mb-4">Get care in four steps</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, i) => (
              <motion.div
                key={step.step}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/20">
                  <span className="text-white font-bold text-lg">{step.step}</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link href="/register">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white border-0 rounded-xl px-8 h-12" data-testid="button-get-started-2">
                Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary border-0">Reviews</Badge>
            <h2 className="text-4xl font-bold mb-4">Loved by patients and providers</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white dark:bg-card rounded-2xl p-6 border border-border shadow-sm"
              >
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                    <span className="text-primary font-semibold text-sm">{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-4 sm:px-6 bg-muted/30">
        <div className="max-w-3xl mx-auto">
          <motion.div custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Frequently asked questions</h2>
          </motion.div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white dark:bg-card rounded-2xl border border-border overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left font-medium hover:bg-accent transition-colors"
                  data-testid={`button-faq-${i}`}
                >
                  {faq.q}
                  <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${openFaq === i ? "rotate-90" : ""}`} />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-6 pb-4 text-muted-foreground text-sm leading-relaxed"
                    >
                      {faq.a}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 bg-gradient-to-br from-primary to-secondary">
        <div className="max-w-3xl mx-auto text-center text-white">
          <motion.h2 custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-4xl font-bold mb-4">
            Start your health journey today
          </motion.h2>
          <motion.p custom={1} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-white/80 text-lg mb-8">
            Join 2 million patients who trust StraightDoc for their care.
          </motion.p>
          <motion.div custom={2} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/register">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90 border-0 rounded-xl px-8 h-12 font-semibold" data-testid="button-cta-register">
                Join as Patient
              </Button>
            </Link>
            <Link href="/register">
              <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 rounded-xl px-8 h-12 font-semibold" data-testid="button-cta-provider">
                Join as Provider
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 bg-card border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Heart className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="font-bold">StraightDoc</span>
            </div>
            <p className="text-sm text-muted-foreground">2024 StraightDoc. All rights reserved.</p>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
              <a href="#" className="hover:text-foreground transition-colors">Terms</a>
              <a href="#" className="hover:text-foreground transition-colors">HIPAA</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function AnimatePresence({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
